import { PageHeader } from "components/ui";
import React from "react";
import { Grid, Typography, Box } from "@mui/material";
import CustomButton from "components/buttonsComponent/StyledButton";
import NavigationButtons from "helper/navigation/NavigationButtons";
import DownloadZipButton from "components/buttonsComponent/DownloadZipButton";

const WhatIsCsrd = () => {
  const urlConfig = {
    name: "Esrs.zip",
    downloadUrl: "https://demo.sustainesg.ca/pdf/ESRS.zip",
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box className="gri-header">
          <NavigationButtons
            imageKey="CSRD/ESRS (EU)"
            title="CSRD-ESRS Reporting"
            activeStep={0}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ borderRadius: "8px", padding: "24px" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#2E7D32",
              mb: 3,
              fontFamily: "DM Sans",
              fontSize: "40px",
              lineHeight: "38px",
            }}
          >
            The global standards for sustainability impacts
          </Typography>

          {/* CSRD Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              component="h4"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 800,
                fontSize: "28px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 1,
              }}
            >
              What is CSRD? — Brief Introduction
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              The Corporate Sustainability Reporting Directive (CSRD) is a
              European Union (EU) regulation that significantly expands
              sustainability reporting requirements for companies operating in
              or linked to the EU.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              It replaces the Non-Financial Reporting Directive (NFRD) and
              applies to a much larger number of companies, including
              non-European companies with significant EU activities.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
              }}
            >
              CSRD requires detailed, auditable disclosures on environmental,
              social, and governance (ESG) matters, aligned with European
              Sustainability Reporting Standards (ESRS). It aims to ensure
              transparency for investors, regulators, and stakeholders about
              companies’ sustainability risks, opportunities, and impacts.
            </Typography>
          </Box>

          {/* ESRS Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              component="h4"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 800,
                fontSize: "28px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 1,
              }}
            >
              What is ESRS? — Brief Introduction
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              The European Sustainability Reporting Standards (ESRS) are the
              detailed technical standards under CSRD that specify what
              companies must disclose and how they should report ESG
              information.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              Developed by EFRAG (European Financial Reporting Advisory Group),
              the ESRS cover:
            </Typography>

            <ul
              style={{
                paddingLeft: "1.5rem",
                marginTop: "8px",
                marginBottom: "8px",
                color: "#4F4F4F",
              }}
            >
              <li>
                General disclosures (e.g., governance, strategy, business model)
              </li>
              <li>
                Environmental topics (e.g., climate change, biodiversity,
                pollution)
              </li>
              <li>Social topics (e.g., workers, communities, human rights)</li>
              <li>Governance topics (e.g., business conduct)</li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
              }}
            >
              These standards are designed to ensure consistent, comparable, and
              verifiable sustainability information across companies and sectors
              in line with EU policy goals.
            </Typography>
          </Box>

          {/* Double Materiality Section */}
          <Box>
            <Typography
              component="h4"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 800,
                fontSize: "28px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 1,
              }}
            >
              What is Double Materiality? (Impact + Financial Materiality)
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              Double materiality is a key reporting concept under CSRD and ESRS,
              combining two perspectives:
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 1,
              }}
            >
              1️⃣ Impact Materiality (Inside-Out)
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              This assesses how the company’s activities impact the environment,
              people, and society — for example, GHG emissions, water use, human
              rights, or community effects. It focuses on the company’s outward
              footprint and sustainability consequences, even if they don’t
              directly affect financial performance.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 1,
              }}
            >
              2️⃣ Financial Materiality (Outside-In)
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
                mb: 2,
              }}
            >
              This assesses how sustainability risks and opportunities affect
              the company’s financial position — for example, climate risks,
              regulatory changes, supply chain disruptions, or reputational
              risks. It focuses on what threatens or benefits the company’s
              value creation, cash flows, and resilience.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
              }}
            >
              ✅ Double materiality means companies must report on both:
            </Typography>

            <ul
              style={{
                paddingLeft: "1.5rem",
                marginTop: "8px",
                marginBottom: "8px",
                color: "#4F4F4F",
              }}
            >
              <li>Their impact on the world (impact materiality)</li>
              <li>The world’s impact on them (financial materiality)</li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-2%",
                color: "#4F4F4F",
              }}
            >
              This approach ensures that sustainability reporting addresses both
              corporate accountability and investor-relevant financial risks,
              supporting a holistic view of sustainability performance.
            </Typography>
          </Box>
        </Box>
        <DownloadZipButton
          config={urlConfig}
          title="Access Link to full ESRS Disclosure"
        />
      </Grid>
    </Grid>
  );
};

export default WhatIsCsrd;
